import React, { useState } from 'react';

export type EventImageShape = 'portrait' | 'square' | 'landscape';

export function classifyImageShape(width: number, height: number): EventImageShape {
    if (!width || !height) return 'square';
    const ratio = width / height;
    if (ratio < 0.88) return 'portrait';
    if (ratio > 1.15) return 'landscape';
    return 'square';
}

const WIDTH_CLASS_HERO: Record<EventImageShape, string> = {
    portrait: 'w-full max-w-[560px]',
    square: 'w-full max-w-[680px]',
    landscape: 'w-full max-w-3xl',
};

const WIDTH_CLASS_CARD: Record<EventImageShape, string> = {
    portrait: 'w-full max-w-[280px]',
    square: 'w-full max-w-[320px]',
    landscape: 'w-full max-w-full',
};

const IconBase = ({ children, size = 20, className = '' }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>
);
const ImageIcon = (props: any) => (
    <IconBase {...props}><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></IconBase>
);

type AdaptiveEventImageProps = {
    src?: string;
    alt: string;
    className?: string;
    frameClassName?: string;
    overlay?: React.ReactNode;
    onErrorDiagnose?: (src: string) => void;
    variant?: 'hero' | 'card';
};

const AdaptiveEventImage: React.FC<AdaptiveEventImageProps> = ({
    src,
    alt,
    className = '',
    frameClassName = '',
    overlay,
    onErrorDiagnose,
    variant = 'hero',
}) => {
    const [failed, setFailed] = useState(false);
    const [shape, setShape] = useState<EventImageShape>('square');
    const hasSrc = Boolean(src?.trim());
    const widthClass = (variant === 'card' ? WIDTH_CLASS_CARD : WIDTH_CLASS_HERO)[shape];

    if (!hasSrc || failed) {
        return (
            <div className={`w-full max-w-[360px] min-h-[220px] flex flex-col items-center justify-center rounded-2xl border border-slate-800 bg-[#111] ${frameClassName}`}>
                <ImageIcon size={32} className="text-slate-600 mb-2" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Event</span>
            </div>
        );
    }

    return (
        <div className={`relative ${widthClass} ${className}`}>
            <img
                src={src}
                alt={alt}
                className="w-full h-auto object-contain block rounded-2xl border border-slate-800/80 bg-[#111]"
                onLoad={(e) => {
                    const img = e.currentTarget;
                    setShape(classifyImageShape(img.naturalWidth, img.naturalHeight));
                }}
                onError={(e) => {
                    onErrorDiagnose?.(e.currentTarget.src);
                    setFailed(true);
                }}
            />
            {overlay ? (
                <div className="absolute bottom-3 left-0 right-0 flex justify-center px-3 pointer-events-none">
                    {overlay}
                </div>
            ) : null}
        </div>
    );
};

export default AdaptiveEventImage;
